/** @prettier */
import classNames from 'classnames';
import { pascalize } from 'humps';
import { List, fromJS } from 'immutable';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import Form from '@xanadu/ui-shared/components/widgets/Form';
import InputField from '@xanadu/ui-shared/components/widgets/Form/inputField';
import { dispatch as gtmDispatch } from '@xanadu/ui-shared/interfaces/googleTagManager';
import {
  userForgotPassword as gtmUserForgotPassword,
  userVirtualPageView as gtmUserVirtualPageView,
} from '@xanadu/ui-shared/interfaces/googleTagManager/actions';
import { getPlatform } from '@xanadu/ui-shared/interfaces/googleTagManager/api';
import { track } from '@xanadu/ui-shared/interfaces/woopra';

import { PRODUCTS } from '../../../constants';

import './Login.scss';

const formatFieldsWithErrors = (currentFields, errors) =>
  errors.reduce((accumulator, item) => {
    const field = item.get('field') || 'username';
    const value = item.get('messages').first();
    const newFields = {
      ...accumulator,
    };

    if (newFields[field]) {
      newFields[field].errors = [value];
    }

    return newFields;
  }, currentFields);

const errorResponse = (field, text) =>
  fromJS([
    {
      // Ensures error appears next to username
      field,
      messages: [text],
    },
  ]);

const validateEmailOrUsername = (username) => {
  if (username === '') {
    return errorResponse('username', 'Email Address or Username cannot be empty');
  }

  return false;
};

const getLoginFields = () => ({
  username: {
    name: 'username',
    type: 'text',
    errors: [],
    label: 'Enter your username',
    value: '',
  },
  password: {
    name: 'password',
    type: 'password',
    label: 'Enter your password',
    errors: [],
    value: '',
  },
});

const getResetPasswordFields = () => ({
  username: {
    name: 'username',
    type: 'text',
    errors: [],
    label: 'Enter your username',
    value: '',
  },
});

const getTwoFactorAuthFields = () => ({
  'mfa-code': {
    name: 'mfa-code',
    type: 'text',
    errors: [],
    label: 'Enter your 2FA code',
    value: '',
  },
});

export default class Login extends Component {
  static get propTypes() {
    return {
      registrationRequest: PropTypes.func.isRequired,
      loginUser: PropTypes.func.isRequired,
      loginRequest: PropTypes.func.isRequired,
      displayModal: PropTypes.func.isRequired,
      onRequestClose: PropTypes.func.isRequired,
      isLoggedIn: PropTypes.bool.isRequired,
      userStatus: PropTypes.string.isRequired,
      loginErrors: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.instanceOf(List),
        PropTypes.string,
      ]),
      loginImage: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
      product: PropTypes.string.isRequired,
      showResetPassword: PropTypes.bool,
      setTermsFields: PropTypes.func.isRequired,
      history: PropTypes.shape({
        push: PropTypes.func.isRequired,
      }).isRequired,
      redirectOnClose: PropTypes.string,
    };
  }

  constructor() {
    super();
    this.state = {
      requestStatusLogin: 'default',
      requestStatusResetPassword: 'default',
      requestStatusTwoFactorAuth: 'default',
      controlsLoginEnabled: false,
      controlsResetPasswordEnabled: false,
      controlsTwoFactorAuthEnabled: false,
      formType: 'login',
      resetPasswordText: '',
      resetPasswordErrors: false,
      loginFields: getLoginFields(),
      loginErrors: false,
      resetPasswordFields: getResetPasswordFields(),
      twoFactorAuthFields: getTwoFactorAuthFields(),
      twoFactorAuthErrors: false,
      verificationError: null,
      resetTimestamp: null,
    };
    this.forgotPassword = this.forgotPassword.bind(this);
    this.onPropertyChange = this.onPropertyChange.bind(this);
    this.resetToLogin = this.resetToLogin.bind(this);
    this.resetPasswordRequest = this.resetPasswordRequest.bind(this);
  }

  componentDidMount() {
    if (this.textRightSection && this.imageRightSection) {
      this.animateRightSection();
    }
  }

  componentWillReceiveProps({ loginErrors, isLoggedIn, showResetPassword }) {
    if (loginErrors) {
      const errorMessage = typeof loginErrors === 'string' ? loginErrors : loginErrors.first();
      const field = errorMessage.get('field');
      let formType = 'login';

      if (field === 'tc-code') {
        this.props.setTermsFields(this.state.loginFields);
      }

      if (field === 'mfa-code') {
        formType = 'two-factor-auth';

        this.setState({
          loginErrors,
          requestStatusLogin: 'default',
          twoFactorAuthFields: getTwoFactorAuthFields(),
          twoFactorAuthText:
            'You have chosen to secure your account with 2 Factor Authentication. Please enter the code provided from your authentication app below.',
          requestStatusTwoFactorAuth: 'default',
          slidingSide: 'normal',
          formType,
        });
      }

      if (errorMessage && typeof errorMessage !== 'string') {
        const message = errorMessage.get('messages').first();
        if (message.indexOf('Incorrect multi factor') > -1) {
          formType = 'two-factor-auth';

          this.setState({
            loginErrors,
            requestStatusLogin: 'default',
            twoFactorAuthFields: getTwoFactorAuthFields(),
            twoFactorAuthText:
              'You have chosen to secure your account with 2 Factor Authentication. Please enter the code provided from your authentication app below.',
            requestStatusTwoFactorAuth: 'default',
            slidingSide: 'normal',
            verificationError: 'Verification code is not recognised',
            formType,
          });
        } else if (message.includes('account is now locked')) {
          formType = 'reset-password';
          this.setState({
            loginErrors,
            requestStatusLogin: 'failed',
            resetPasswordFields: getResetPasswordFields(),
            resetPasswordText:
              'Your account has been locked due to three failed password attempts.',
            requestStatusResetPassword: 'default',
            slidingSide: 'normal',
            formType,
          });

          return;
        }
      }

      this.setState({
        loginErrors,
        requestStatusLogin: field === 'mfa-code' ? 'default' : 'failed',
        formType,
      });
    } else if (isLoggedIn) {
      this.props.displayModal({
        show: false,
        section: 'default',
      });

      this.setState({
        loginFields: getLoginFields(),
        requestStatusLogin: 'default',
        controlsLoginEnabled: false,
      });
    } else if (!isLoggedIn && showResetPassword) {
      this.forgotPassword();
    }
  }

  componentWillUnmount() {
    const { history, isLoggedIn, redirectOnClose } = this.props;

    clearTimeout(this.timeoutResetSuccessFields);
    this.timeoutResetSuccessFields = null;
    this.props.loginRequest(false);
    this.state.resetTimestamp = null;

    if (redirectOnClose && !isLoggedIn) {
      history.push(redirectOnClose);
    }
  }

  onPropertyChange(data) {
    const newState = {
      ...this.state,
    };

    let formFields;
    if (this.state.formType === 'login') {
      formFields = this.state.loginFields;
      newState.controlsLoginEnabled = true;
    } else if (this.state.formType === 'reset-password') {
      formFields = this.state.resetPasswordFields;
    } else if (this.state.formType === 'two-factor-auth') {
      formFields = this.state.twoFactorAuthFields;
    }

    const newFormFields = Object.keys(formFields).reduce(
      (accumulator, key) => ({
        ...accumulator,
        [key]: { ...accumulator[key] },
      }),
      formFields
    );

    if (this.state.formType === 'login') {
      newState.loginFields = newFormFields;
    } else if (this.state.formType === 'reset-password') {
      newState.resetPasswordFields = newFormFields;
      newState.controlsResetPasswordEnabled = true;
    } else if (this.state.formType === 'two-factor-auth') {
      newState.controlsTwoFactorAuthEnabled = true;
      newState.twoFactorAuthFields = newFormFields;
    }

    newFormFields[data.name].value = data.value;

    this.setState(newState);
  }

  getFormattedLoginFields() {
    // Takes Immutable list from this.loginErrors and returns correctly formatted array
    if (!this.props.loginErrors || !this.state.loginErrors) {
      return this.state.loginFields;
    }

    const currentFields = {
      username: {
        ...this.state.loginFields.username,
        errors: [],
      },
      password: {
        ...this.state.loginFields.password,
        errors: [],
      },
    };

    return formatFieldsWithErrors(currentFields, this.props.loginErrors);
  }

  getFormattedResetFields() {
    // Takes Immutable list from this.loginErrors and returns correctly formatted array
    if (!this.state.resetPasswordErrors) {
      return this.state.resetPasswordFields;
    }

    const currentFields = {
      username: {
        ...this.state.resetPasswordFields.username,
        errors: [],
      },
    };

    return formatFieldsWithErrors(currentFields, this.state.resetPasswordErrors);
  }

  getFormattedTwoFactorAuthFields() {
    // Takes Immutable list from this.loginErrors and returns correctly formatted array
    if (!this.state.twoFactorAuthErrors) {
      return this.state.twoFactorAuthFields;
    }

    const currentFields = {
      'mfa-code': {
        ...this.state.twoFactorAuthFields['mfa-code'],
        errors: [],
      },
    };

    return formatFieldsWithErrors(currentFields, this.state.twoFactorAuthErrors);
  }

  getLoginForm() {
    const fields = this.getFormattedLoginFields();
    return (
      <div key="login" className="mb-login__form">
        <Form
          type="login"
          primaryButtonEnabled={this.state.controlsLoginEnabled}
          secondaryButtonEnabled={this.state.requestStatusLogin !== 'processing'}
          contentPrimaryButton="Login"
          contentSecondaryButton="Trouble Logging In?"
          onPrimaryButtonClick={() => this.signin()}
          onSecondaryButtonClick={this.forgotPassword}
          primaryButtonType="primary"
          primaryButtonSizeType="wider"
          secondaryButtonType="flat"
          secondaryButtonSizeType="wider"
          primaryDataHook="loginbutton"
          secondaryDataHook="loginforgotpassword"
          requestStatus={this.state.requestStatusLogin}
        >
          <InputField
            key="username"
            label="Username"
            name="username"
            theme="dark"
            type="text"
            value={fields.username.value}
            focusOnMount={getPlatform() === 'desktop'}
            onPropertyChange={this.onPropertyChange}
            errors={fields.username.errors}
            dataHook="loginusername"
          />
          <InputField
            key="password"
            label="Password"
            name="password"
            theme="dark"
            type="password"
            value={fields.password.value}
            onPropertyChange={this.onPropertyChange}
            errors={fields.password.errors}
            dataHook="loginpassword"
          />
        </Form>
      </div>
    );
  }

  getResetPasswordForm() {
    const { formType, resetPasswordText, requestStatusResetPassword } = this.state;
    const fields = this.getFormattedResetFields();
    const passwordResetSuccess = requestStatusResetPassword === 'done';
    let contentResetPasswordText =
      'If you are having trouble logging in, please enter your registered email address or username below.';

    if (resetPasswordText) {
      contentResetPasswordText = resetPasswordText;
    }

    if (passwordResetSuccess) {
      contentResetPasswordText =
        'A password reset link has been sent to your registered e-mail address.';
    }

    return (
      <div key="reset-password" className="mb-login__form">
        <div className="mb-login__container-reset-password-text">
          {contentResetPasswordText}
          <br />
          <br />
          {!resetPasswordText &&
            'You will receive an email with your username, and a link to reset your password.'}
          {formType === 'reset-password' &&
            !passwordResetSuccess &&
            resetPasswordText &&
            'Enter your username below and we will send a reset link to your registered email address.'}
        </div>
        <Form
          key="reset-password"
          type="reset-password"
          primaryButtonEnabled={this.state.controlsResetPasswordEnabled}
          secondaryButtonEnabled
          contentPrimaryButton={resetPasswordText ? 'Unlock Account' : 'Recover Account'}
          contentSecondaryButton="Cancel"
          onPrimaryButtonClick={this.resetPasswordRequest}
          onSecondaryButtonClick={this.resetToLogin}
          primaryButtonType="primary"
          primaryButtonSizeType="wider"
          secondaryButtonType="flat"
          secondaryButtonSizeType="wider"
          requestStatus={this.state.requestStatusResetPassword}
        >
          <InputField
            key="username"
            label="Email Address or Username"
            name="username"
            focusOnMount={getPlatform() === 'desktop'}
            theme="dark"
            type="text"
            value={fields.username.value}
            onPropertyChange={this.onPropertyChange}
            errors={fields.username.errors}
          />
        </Form>
      </div>
    );
  }

  getTwoFactorAuthForm() {
    const { twoFactorAuthText } = this.state;
    const fields = this.getFormattedTwoFactorAuthFields();

    return (
      <div key="two-factor-auth" className="mb-login__form">
        <div className="mb-login__container-reset-password-text">{twoFactorAuthText}</div>
        <Form
          key="2fa"
          type="2fa"
          primaryButtonEnabled={this.state.controlsTwoFactorAuthEnabled}
          secondaryButtonEnabled
          contentPrimaryButton="Verify"
          contentSecondaryButton="Cancel"
          onPrimaryButtonClick={() => this.signin(true)}
          onSecondaryButtonClick={() =>
            this.props.displayModal({
              show: false,
              section: 'default',
            })
          }
          primaryButtonType="primary"
          primaryButtonSizeType="wider"
          secondaryButtonType="flat"
          secondaryButtonSizeType="wider"
          requestStatus={this.state.requestStatusLogin}
          primaryDataHook="2fasubmit"
          secondaryDataHook="2facancel"
        >
          <InputField
            key="mfa-code"
            label="Verification Code"
            name="mfa-code"
            focusOnMount={getPlatform() === 'desktop'}
            theme="dark"
            type="text"
            value={fields['mfa-code'].value}
            onPropertyChange={this.onPropertyChange}
            errors={fields['mfa-code'].errors}
            dataHook="2facode"
          />
          {this.state.verificationError ? (
            <div className="mb-login__mfa-error">{this.state.verificationError}</div>
          ) : null}
        </Form>
      </div>
    );
  }

  animateRightSection() {
    const textSection = this.textRightSection;
    const imageSection = this.imageRightSection;
    setTimeout(() => {
      textSection.classList.add('mb-login__text-right-section--animate');
      setTimeout(() => {
        imageSection.classList.add('mb-login__image-right-section--animate');
      }, 150);
    }, 250);
  }

  signin(withTwoFactor) {
    const { username, password } = this.getFormattedLoginFields('loginFields');
    let twoFactorAuthCode = {};
    let twoFactorAuthCodeValue = '';

    if (withTwoFactor) {
      const fields = this.getFormattedTwoFactorAuthFields();
      twoFactorAuthCode = fields['mfa-code'];
      twoFactorAuthCodeValue = twoFactorAuthCode.value
        ? twoFactorAuthCode.value.replace(/ /g, '')
        : '';
    }

    const payload = {
      loginType: 'login',
      loginPayload: {
        username: username.value,
        password: password.value,
        twoFactorAuthCode: twoFactorAuthCodeValue,
      },
    };
    this.props.loginUser(payload);

    const loginFields = {
      username: {
        ...this.state.loginFields.username,
        value: username.value,
      },
      password: {
        ...this.state.loginFields.password,
        value: password.value,
      },
    };

    this.setState({
      requestStatusLogin: 'processing',
      loginFields,
    });
  }

  resetPasswordRequest = () => {
    const now = Date.now();
    if (
      this.state.requestStatusResetPassword === 'processing' ||
      (this.state.resetTimestamp && now < this.state.resetTimestamp + 5000)
    ) {
      return;
    }
    this.setState({ resetTimestamp: now });

    let resetPasswordErrors = false;
    const url = '/bpapi/rest/security/v2/password-reset';
    const PUT_OPTIONS = {
      credentials: 'same-origin',
      method: 'POST',
      body: JSON.stringify({
        'user-identifier': this.state.resetPasswordFields.username.value,
      }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };

    // Username validation
    if (this.state.formType === 'reset-password') {
      resetPasswordErrors = validateEmailOrUsername(this.state.resetPasswordFields.username.value);
    }

    if (resetPasswordErrors) {
      this.setState({
        resetPasswordErrors,
        controlsResetPasswordEnabled: !resetPasswordErrors,
      });
      return;
    }

    this.setState({
      requestStatusResetPassword: 'processing',
    });

    fetch(url, PUT_OPTIONS)
      .then((response) => {
        if (!response.ok) {
          return Promise.reject(response);
        }
        return Promise.resolve(response.json());
      })
      .then(() => {
        // NOTE: We need to trigger the GTM event to send some data to dataLayerPush()
        gtmDispatch(
          gtmUserForgotPassword({ userForgot: this.state.resetPasswordFields.username.value })
        );

        this.setState({
          requestStatusResetPassword: 'done',
          resetPasswordErrors: false,
        });
        this.timeoutResetSuccessFields = setTimeout(() => {
          this.props.onRequestClose();
        }, 3000);
      })
      .catch((response) => {
        response.json().then(({ errors }) => {
          if (errors) {
            this.setState({
              requestStatusResetPassword: 'failed',
              controlsResetPasswordEnabled: false,
            });
          }
        });
      });
  };

  forgotPassword() {
    this.setState({
      formType: 'reset-password',
      loginFields: getLoginFields(),
      resetPasswordFields: getResetPasswordFields(),
      loginErrors: false,
      resetPasswordErrors: false,
      controlsResetPasswordEnabled: false,
      requestStatusResetPassword: 'default',
    });
  }

  resetToLogin() {
    this.setState({
      formType: 'login',
      resetPasswordText: '',
      loginFields: getLoginFields(),
    });
  }

  renderForm() {
    const { formType } = this.state;
    let component = null;

    switch (formType) {
      case 'login':
        component = this.getLoginForm();
        break;
      case 'reset-password':
        component = this.getResetPasswordForm();
        break;
      case 'two-factor-auth':
        component = this.getTwoFactorAuthForm();
        break;
      default:
        break;
    }

    return (
      <CSSTransition appear classNames={`sliding-${formType}`} timeout={400} key={formType}>
        {component}
      </CSSTransition>
    );
  }

  render() {
    const { userStatus, loginRequest, redirectOnClose, registrationRequest } = this.props;

    let backgroundImage = {};
    let dynamicLoginImageClass = '';

    const currentUrl = window.location.href;

    const loginImageKey = `loginImage${this.props.product === PRODUCTS.exchange ? '' : pascalize(this.props.product.toLowerCase())}`;

    const userIsLocked = userStatus === 'locked';

    if (this.props.loginImage) {
      dynamicLoginImageClass = 'mb-login__section--dynamicImage';

      backgroundImage = { backgroundImage: `url(${this.props[loginImageKey]})` };
    }

    const loginClass = classNames({
      'mb-login': true,
      'mb-login--hideRightSection': userIsLocked,
    });

    return (
      <div className={loginClass}>
        <div className="mb-login__section mb-login__section--left">
          <div className="mb-login__container-logo" />
          <div className="mb-login__container-form">
            <TransitionGroup component="span" timeout={400}>
              {this.renderForm()}
            </TransitionGroup>
          </div>
        </div>
        {!userIsLocked && (
          <div
            role="button"
            id="mb-join-now-right-image"
            className={`mb-login__section mb-login__section--right ${dynamicLoginImageClass}`}
            style={backgroundImage}
            onClick={() => {
              this.resetToLogin();
              loginRequest(false, { redirectOnClose });
              setTimeout(() => {
                registrationRequest({ show: true, promoRegistrationImage: null, checked: false });

                // GTM
                gtmDispatch(
                  gtmUserVirtualPageView({
                    currentUrl,
                    eventName: 'Reg Process',
                    virtualPageTitle: 'Step 1 - account',
                    virtualPageURL: '/reg/step1/',
                    previousURL: '/login/',
                    clickSource: { clickSource: `mb-join-now-right-image_${location.href}` },
                  })
                );
                // ===
                track('Registration', 'Registration opened', {
                  description: 'login',
                });
              }, 250);
            }}
          >
            <div
              className="mb-login__text-right-section"
              ref={(node) => {
                this.textRightSection = node;
              }}
            />
            <div
              className="mb-login__image-right-section"
              ref={(node) => {
                this.imageRightSection = node;
              }}
            />
          </div>
        )}
      </div>
    );
  }
}
